import { useMemo } from 'react';

import { GetHouseholdFamilyTreeQuery } from '../graphql/FamilyTree.generated';

export function useRemoveHiddenIndividuals(
  familyTreeData: GetHouseholdFamilyTreeQuery | undefined
): {
  familyTreeData: GetHouseholdFamilyTreeQuery['familyTree'];
  clientProfiles: GetHouseholdFamilyTreeQuery['clientProfiles'];
} {
  const people = familyTreeData?.familyTree?.people;
  const showHiddenIndividuals =
    familyTreeData?.familyTree?.settings.showHiddenIndividuals;
  const hiddenIndividualIds: Set<string> = useMemo(() => {
    if (showHiddenIndividuals === false) {
      const individualIds = (people ?? []).flatMap((person) => {
        return person.nodeSettings.showIndividualOnTree === false
          ? person.clientProfile.id
          : [];
      });

      return new Set(individualIds);
    }

    return new Set<string>();
  }, [people, showHiddenIndividuals]);

  const finalFamilyTreeData = useMemo(() => {
    if (!familyTreeData?.familyTree) {
      return familyTreeData?.familyTree;
    }

    return {
      ...familyTreeData?.familyTree,
      people:
        familyTreeData?.familyTree?.people?.filter(
          (person) => !hiddenIndividualIds.has(person.clientProfile.id)
        ) ?? [],
      marriages:
        familyTreeData?.familyTree?.marriages?.flatMap((marriage) => {
          const spouses = marriage.spouses.filter(
            (spouse) => !hiddenIndividualIds.has(spouse ?? '')
          );
          const children = marriage.children.filter(
            (child) => !hiddenIndividualIds.has(child ?? '')
          );

          if (spouses.length === 0) {
            return [];
          }

          return {
            ...marriage,
            spouses,
            children,
          };
        }) ?? [],
    };
  }, [familyTreeData?.familyTree, hiddenIndividualIds]);

  const finalClientProfiles = useMemo(() => {
    if (!familyTreeData) {
      return {
        __typename: 'ClientProfileConnection' as const,
        edges: [],
      };
    }

    return {
      ...familyTreeData.clientProfiles,
      edges:
        familyTreeData.clientProfiles?.edges?.filter(
          (profile) => !hiddenIndividualIds.has(profile?.node?.id ?? '')
        ) ?? [],
    };
  }, [familyTreeData, hiddenIndividualIds]);

  return {
    familyTreeData: finalFamilyTreeData,
    clientProfiles: finalClientProfiles,
  };
}
